/**
 * Business Process Create Route
 *
 * Create a new business process using BusinessProcessForm component
 */

import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useApolloClient } from '@apollo/client/react';
import { BusinessProcessForm } from '../../components/business-processes';
import {
  createBusinessProcess,
  type CreateBusinessProcessInput,
  ApiError,
} from '../../lib/apiClient';
import type { CreateBusinessProcessFormData } from '../../components/forms';
import './new.css';

export const Route = createFileRoute('/business-processes/new')({
  component: BusinessProcessCreatePage,
});

function BusinessProcessCreatePage(): JSX.Element {
  const BannerElement = 'forge-banner' as React.ElementType;
  const IconElement = 'forge-icon' as React.ElementType;
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (formData: CreateBusinessProcessFormData): Promise<void> => {
    setIsSubmitting(true);
    setError(null);

    try {
      // Build create input
      const input: CreateBusinessProcessInput = {
        name: formData.name,
        description: formData.description || undefined,
        processType: formData.processType,
        processTier: formData.processTier,
        processOwnerId: formData.processOwnerId,
        owningBusinessUnitId: formData.owningBusinessUnitId,
        inputDescription: formData.inputDescription || undefined,
        outputDescription: formData.outputDescription || undefined,
        estimatedDuration: formData.estimatedDuration || undefined,
        volumePerPeriod: formData.volumePerPeriod || undefined,
        automationLevel: formData.automationLevel || undefined,
        toolingDescription: formData.toolingDescription || undefined,
        status: formData.status || undefined,
        tags: formData.tags && formData.tags.length > 0 ? formData.tags : undefined,
        notes: formData.notes || undefined,
      };

      const newEntity = await createBusinessProcess(input);

      // Refetch queries to update cache
      await apolloClient.refetchQueries({
        include: ['ListBusinessProcesses'],
      });

      // Navigate to the new business process detail page
      navigate({
        to: `/business-processes/${newEntity.entityId}` as string,
      });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
      setIsSubmitting(false);
    }
  };

  const handleCancel = (): void => {
    navigate({ to: '/business-processes', search: { page: 1, pageSize: 25 } });
  };

  return (
    <div className="business-process-create-page">
      <div className="page-header page-header--stacked">
        <h1 className="forge-typography--headline4">Create Business Process</h1>
        <p className="forge-typography--body2 page-header-subtitle">Add a new business process</p>
      </div>

      {error && (
        <BannerElement theme="error" dismissed={false} className="error-banner" role="alert">
          <div className="banner-content">
            <IconElement name="error" aria-hidden="true" />
            <span>{error}</span>
          </div>
        </BannerElement>
      )}

      <BusinessProcessForm
        mode="create"
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isSubmitting={isSubmitting}
      />
    </div>
  );
}
